import React, { useEffect, useRef, useState, JSX } from "react";
import BackgroundImage from "../../../components/ui/BackgroundImage";
import bgImage from "../assets/images/bg.webp";
import carImage from "../assets/images/car.webp";
import "../style.css";
import Heartbeat from "./Heartbeat";
import ProgressBar from "./ProgressBar";
import { GameResultStatus, CenterFX, StylesCfg } from "../types";

import validMoveSound from "../assets/audio/success-move.wav";
import invalidMoveSound from "../assets/audio/invalid-move.wav";
import gameWinSound from "../assets/audio/game-win.wav";
import gameOverSound from "../assets/audio/game-over.wav";
import heartBeatSound from "../assets/audio/heartbeat.wav";

import { useAudioEffects } from "../../../hooks/use-audio-effects";
import GameResults from "./GameResults";
import { CenterZone } from "./CenterZone";
import { Lines } from "./Lines";
import { DEFAULTS, LINE_ANIM_DURATION_MS } from "../constants";
import { useBeatLoop } from "../hooks/use-beat-loop";
import Introduction from "./Introduction";

interface Props {
  className?: string;
  onSuccess?: () => void;
  onFail?: () => void;
  styles?: StylesCfg;
  successMessage?: string;
  failureMessage?: string;
  successTitle?: string;
  failureTitle?: string;
  soundOptions?: {
    includeHeartBeat: boolean;
  };
}

export default function ResurectMinigame({
  className = "",
  onSuccess,
  onFail,
  styles,
  successMessage = "Great timing!",
  failureMessage = "You missed a lot of heartbeats.",
  successTitle = "SUCCESS",
  failureTitle = "FAIL",
  soundOptions = { includeHeartBeat: true },
}: Props): JSX.Element {
  // ---- Config (overridable via props) ----
  const TARGET_SUCCESS_SCORE = DEFAULTS.TARGET_SUCCESS_SCORE;
  const SUCCESS_INCREMENT = DEFAULTS.SUCCESS_INCREMENT;
  const PENALTY_INCREMENT = DEFAULTS.PENALTY_INCREMENT;
  const CENTER_DIV_WIDTH_PX =
    styles?.centerWidthPx ?? DEFAULTS.CENTER_DIV_WIDTH_PX;
  const LINE_WIDTH_PX = styles?.lineWidthPx ?? DEFAULTS.LINE_WIDTH_PX;

  const { playEffect, stopEffect } = useAudioEffects();

  // ---- State/refs ----
  const [successProgress, setSuccessProgress] = useState<number>(0);
  const [failureProgress, setFailureProgress] = useState<number>(0);
  // added isRunning state to control initial start overlay
  const [isRunning, setIsRunning] = useState<boolean>(false);
  // isAnimating will now be started when the user presses Start
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [gameResultStatus, setGameResultStatus] =
    useState<GameResultStatus>(null);
  const [centerFX, setCenterFX] = useState<CenterFX>("idle");

  const monitorRef = useRef<HTMLDivElement | null>(null);
  const centerRef = useRef<HTMLDivElement | null>(null);
  const leftLineRef = useRef<HTMLDivElement | null>(null);
  const rightLineRef = useRef<HTMLDivElement | null>(null);

  // keep these refs for cross-hook/state checks
  const isAnimatingRef = useRef<boolean>(isAnimating);
  const gameResultRef = useRef<GameResultStatus>(gameResultStatus);
  const successProgressRef = useRef<number>(0);
  const failureProgressRef = useRef<number>(0);

  const beatLoop = useBeatLoop({
    monitorRef,
    centerRef,
    leftLineRef,
    rightLineRef,
    LINE_WIDTH_PX,
    TARGET_SUCCESS_SCORE,
    LINE_ANIM_DURATION_MS,
    isAnimatingRef,
    gameResultRef,
    onNoReaction: handleNoReaction,
    onValidMove: handleValidMove,
    onInvalidMove: handleInvalidMove,
    playEffect,
    invalidMoveSoundLocal: invalidMoveSound,
  });

  useEffect(() => {
    isAnimatingRef.current = isAnimating;
  }, [isAnimating]);
  useEffect(() => {
    gameResultRef.current = gameResultStatus;
  }, [gameResultStatus]);
  useEffect(() => {
    successProgressRef.current = successProgress;
  }, [successProgress]);
  useEffect(() => {
    failureProgressRef.current = failureProgress;
  }, [failureProgress]);

  // ---- FX helper ----
  function flashCenter(kind: CenterFX) {
    setCenterFX(kind);
    window.setTimeout(() => setCenterFX("idle"), 220);
  }

  // ---- End/reset ----
  function endGame(result: GameResultStatus) {
    setGameResultStatus(result);
    setIsAnimating(false);
    stopEffect({ inBackground: true });
    isAnimatingRef.current = false;
    gameResultRef.current = result;

    if (result === "success") {
      playEffect(gameWinSound, { volume: 0.5 });
      onSuccess?.();
    } else if (result === "failure") {
      playEffect(gameOverSound);
      onFail?.();
    }
  }

  function resetGame() {
    setSuccessProgress(0);
    successProgressRef.current = 0;
    setFailureProgress(0);
    failureProgressRef.current = 0;
    setGameResultStatus(null);
    setIsAnimating(true);
    isAnimatingRef.current = true;
    gameResultRef.current = null;
    setCenterFX("idle");
    // ask the beat loop to restart
    requestAnimationFrame(() => {
      beatLoop.resetLoop();
    });
  }

  // start the game when user presses the start button
  function startGame() {
    setIsRunning(true);
    setIsAnimating(true);

    if (soundOptions.includeHeartBeat) {
      playEffect(heartBeatSound, { inBackground: true, loop: true });
    }
    isAnimatingRef.current = true;
    // kick the beat loop so it begins cleanly
    requestAnimationFrame(() => {
      beatLoop.resetLoop();
    });
  }

  // ---- handlers that will be passed to the hook ----
  function handleNoReaction() {
    // no reaction: play invalid sound, mark brief no-reaction, apply penalty
    // (hook already plays the invalid sound once before calling this, but keep
    // the rest of the logic here to preserve original behavior)
    setFailureProgress((prev) => {
      const next = Math.min(TARGET_SUCCESS_SCORE, prev + PENALTY_INCREMENT);
      failureProgressRef.current = next;

      flashCenter("miss");

      if (next >= TARGET_SUCCESS_SCORE) endGame("failure");
      return next;
    });
  }

  function handleValidMove() {
    playEffect(validMoveSound);
    flashCenter("success");
    setSuccessProgress((prev) => {
      const next = Math.min(TARGET_SUCCESS_SCORE, prev + SUCCESS_INCREMENT);
      successProgressRef.current = next;
      if (next >= TARGET_SUCCESS_SCORE) endGame("success");
      return next;
    });
  }

  function handleInvalidMove() {
    playEffect(invalidMoveSound);
    flashCenter("fail");
    setFailureProgress((prev) => {
      const next = Math.min(TARGET_SUCCESS_SCORE, prev + PENALTY_INCREMENT);
      failureProgressRef.current = next;
      if (next >= TARGET_SUCCESS_SCORE) endGame("failure");
      return next;
    });
  }

  // ---- initialize hook (beat loop + keyboard handling) ----

  return (
    <BackgroundImage
      imageSrc={carImage}
      className={`h-screen flex justify-center items-end pb-10 ${className}`}
    >
      {/* END-OF-GAME OVERLAY */}
      {gameResultStatus && (
        <GameResults
          gameResultStatus={gameResultStatus}
          resetGame={resetGame}
          successTitle={successTitle}
          failureTitle={failureTitle}
          successMessage={successMessage}
          failureMessage={failureMessage}
        />
      )}

      <div className="flex relative items-center justify-center space-x-3 h-[250px]">
        <ProgressBar progress={successProgress} variant="success" />

        <BackgroundImage
          imageSrc={bgImage}
          imageClassName="rounded-xl"
          className="rounded-xl h-full"
        >
          <div
            style={
              isRunning
                ? {
                    WebkitMaskImage:
                      "linear-gradient(to right, transparent 0%, black 25%, black 85%, transparent 100%)",
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskSize: "100% 100%",
                    maskImage:
                      "linear-gradient(to right, transparent 0%, black 25%, black 85%, transparent 100%)",
                    maskRepeat: "no-repeat",
                    maskSize: "100% 100%",
                  }
                : {}
            }
            ref={monitorRef}
            className="overflow-hidden relative h-full rounded-xl min-w-[638px]"
          >
            {/* START-OVERLAY: covers entire screen until user presses Start */}
            {!isRunning && <Introduction startGame={startGame} />}

            <Heartbeat lineColor="#6691FF" speed={3.5} />

            {/* CENTER ZONE w/ FX */}

            <CenterZone
              ref={centerRef}
              centerWidthPx={CENTER_DIV_WIDTH_PX}
              centerFX={centerFX}
              styles={styles}
            />

            {/* LINES */}
            <Lines
              leftRef={leftLineRef}
              rightRef={rightLineRef}
              lineWidthPx={LINE_WIDTH_PX}
            />
          </div>
        </BackgroundImage>

        <ProgressBar progress={failureProgress} variant="fail" />
      </div>
    </BackgroundImage>
  );
}
